import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { InvoiceHire, InvoiceHireItem } from '../../types';
import Button from './Button';
import { formatDateForAPI, formatDateForInput, formatMonthForAPI, formatMonthForInput } from '../../utils/dateFormatter';
import { findCodePrefix, nextSequence } from '../../utils/codePrefixUtils';
import { resolveCurrencyDocumentId } from '../../utils/currencyUtils';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { useFOAs } from '../../hooks/useFOAs';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import { useInvoiceHireEditPrefill } from './useInvoiceHireEditPrefill';
import InvoiceHireHeaderSection from './InvoiceHireHeaderSection';
import InvoiceHireDebitNoteContent from './InvoiceHireDebitNoteContent';
import InvoiceHireItemsTable from './InvoiceHireItemsTable';
import InvoiceHireSocialInsurance from './InvoiceHireSocialInsurance';
import InvoiceHireSignaturesSection from './InvoiceHireSignaturesSection';

interface InvoiceHireFormProps {
  invoiceHire?: InvoiceHire;
  onSubmit: (data: Partial<InvoiceHire>) => void;
  onCancel: () => void;
  /** Invoice type pre-selected when creating (e.g. 'debit_note' from the Debit Notes page) */
  defaultType?: InvoiceHire['type'];
}

const getDefaultDescription = (type: string) => {
  switch (type) {
    case 'invoice_accommodation': return 'meals';
    case 'invoice_hire': return 'Hire\nHiring of AHTS Petro Provider';
    case 'debit_note': return 'Debit Note Adjustment';
    case 'credit_note': return 'Credit Note Adjustment';
    default: return 'Hire';
  }
};

const getDefaultUnit = (type: string) =>
  type === 'invoice_accommodation' ? 'Meal'
    : (type === 'debit_note' || type === 'credit_note') ? 'Adjustment' : 'Day';

const modeOfPayOptions = [
  { value: 'cash', label: 'Cash' }, { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'check', label: 'Check' }, { value: 'credit_card', label: 'Credit Card' }
];

const statusOptions = [
  { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' }
];

const invoiceTypeOptions = [
  { value: 'debit_note', label: 'Debit' },
  { value: 'credit_note', label: 'Credit' },
  { value: 'invoice_hire', label: 'Invoice Hire' }, { value: 'invoice_accommodation', label: 'Invoice Accommodation' }
];

const InvoiceHireForm: React.FC<InvoiceHireFormProps> = ({ invoiceHire, onSubmit, onCancel, defaultType }) => {
  const { data: currencies } = useCurrencies();
  const clientQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: clients = [] } = useClients(clientQO);
  const branchQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: branches = [] } = useBranches(branchQO);
  const foaQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: foas = [] } = useFOAs(foaQO);
  const cpQO = useMemo(() => ({ sort: [{ field: 'createdAt', order: 'desc' as const }], pagination: { pageSize: 100 } }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(cpQO);

  const [selectedCodePrefix, setSelectedCodePrefix] = useState('');
  const [invoiceSeqNumber, setInvoiceSeqNumber] = useState('001');

  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) return [{ value: '', label: 'Select Currency' }];
    return [
      { value: '', label: 'Select Currency' },
      ...currencies.filter((c: any) => c.isActive !== false)
        .map((c: any) => ({ value: c.documentId || c.id || '', label: c.code || '' }))
    ];
  }, [currencies]);

  const clientOptions = useMemo(() => [
    { value: '', label: 'Select Customer' },
    ...clients.map((c: any) => ({ value: c.documentId || c.id, label: c.name || '' }))
  ], [clients]);

  const vesselOptions = useMemo(() => [
    { value: '', label: 'Select vessel (optional)' },
    ...branches.map((b: any) => ({ value: b.documentId || b.id, label: b.name || '' }))
  ], [branches]);

  const foaOptions = useMemo(() => [
    { value: '', label: 'Select FOA (Optional)' },
    ...foas.map((f: any) => ({ value: f.documentId || f.id, label: `${f.foaNumber || ''} - ${f.englishName || ''}` }))
  ], [foas]);

  const { register, handleSubmit, formState: { errors, isSubmitting }, control, watch, setValue } = useForm<InvoiceHire>({
    defaultValues: invoiceHire ? {
      ...invoiceHire, invoiceDate: formatDateForInput(invoiceHire.invoiceDate),
      operationDate: formatMonthForInput(invoiceHire.operationDate),
      currency: typeof invoiceHire.currency === 'object' && invoiceHire.currency !== null
        ? ((invoiceHire.currency as any).documentId || String((invoiceHire.currency as any).id))
        : (invoiceHire.currency || ''),
      items: invoiceHire.items?.map(item => ({
        ...item, fromDate: formatDateForInput(item.fromDate), toDate: formatDateForInput(item.toDate)
      })) || [],
      socialInsurance: {
        ...invoiceHire.socialInsurance,
        clientDeliveryDate: formatDateForInput(invoiceHire.socialInsurance?.clientDeliveryDate),
        insuranceDeliveryDate: formatDateForInput(invoiceHire.socialInsurance?.insuranceDeliveryDate),
        clientReceiptDate: formatDateForInput(invoiceHire.socialInsurance?.clientReceiptDate)
      }
    } : {
      invoiceNumber: '', invoiceDate: new Date().toISOString().split('T')[0],
      operationDate: new Date().toISOString().slice(0, 7), customerId: '', contractNumber: '',
      projectId: '', vesselId: '', foaId: '', telephone: '', modeOfPay: 'bank_transfer',
      type: defaultType || 'invoice_hire', freeTextContent: '',
      items: [{ id: '1', itemNumber: 1, description: '', fromDate: new Date().toISOString().split('T')[0],
        toDate: new Date().toISOString().split('T')[0], unit: 'Day', quantity: 1, rateUSD: 0, amountUSD: 0 }],
      netTotal: 0, currency: '', exchangeRate: 1,
      financeManager: { name: 'Mohamed Hamdy', signature: '' },
      generalManager: { name: 'Capt. Walid Gouda', signature: '' },
      status: 'draft', notes: '',
      socialInsurance: { insuranceStatus: 'not_received', clientDeliveryDate: '', insuranceDeliveryDate: '',
        certificateNumber: '', certificateValue: 0, clientReceiptDate: '' }
    }
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  // Re-apply saved customer/vessel/currency (incl. per-item currency) once
  // the option lists load, otherwise the selects show their placeholder
  useInvoiceHireEditPrefill(invoiceHire, clients, branches, currencies as any[], setValue as any);

  useEffect(() => {
    if (invoiceHire?.invoiceNumber && (codePrefixes as any[]).length > 0) {
      const match = (codePrefixes as any[]).find((p: any) => invoiceHire.invoiceNumber.startsWith(p.prefix));
      if (match) { setSelectedCodePrefix(match.id); setInvoiceSeqNumber(invoiceHire.invoiceNumber.replace(match.prefix, '')); }
    }
  }, [invoiceHire, codePrefixes]);

  const watchedItems = watch('items');
  const invoiceType = watch('type');
  const selectedFoaId = useWatch({ control, name: 'foaId' });
  const isDebitNote = invoiceType === 'debit_note';
  const isDebitOrCredit = isDebitNote || invoiceType === 'credit_note';

  useEffect(() => {
    if (!invoiceHire) {
      const ts = Date.now().toString().slice(-6);
      const pm: Record<string, string> = { debit_note: 'DN', credit_note: 'CN', invoice_hire: 'HIRE', invoice_accommodation: 'ACM' };
      setValue('invoiceNumber', `${pm[invoiceType] || 'INV'}-${ts}`);
    }
  }, [invoiceHire, invoiceType, setValue]);

  // Auto-fill vessel, contract number and currency from the selected FOA.
  // Only runs when the FOA actually changes, so editing an existing invoice
  // doesn't overwrite its stored values on mount.
  const prevFoaIdRef = useRef<string | undefined>(invoiceHire?.foaId || undefined);

  // Re-apply the saved FOA once the FOA list has loaded (edit mode), so the
  // dropdown pre-selects it. prevFoaIdRef is updated first so the auto-fill
  // effect below doesn't overwrite the invoice's stored values.
  useEffect(() => {
    if (!invoiceHire?.foaId || foas.length === 0) return;
    const match: any = foas.find(
      (f: any) => f.documentId === invoiceHire.foaId || String(f.id) === String(invoiceHire.foaId)
    );
    if (!match) return;
    const normalized = match.documentId || String(match.id);
    prevFoaIdRef.current = normalized;
    setValue('foaId', normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foas, invoiceHire]);
  useEffect(() => {
    if (selectedFoaId === prevFoaIdRef.current) return;
    prevFoaIdRef.current = selectedFoaId as string | undefined;
    if (!selectedFoaId || foas.length === 0) return;
    const foa: any = foas.find((f: any) => (f.documentId || f.id) === selectedFoaId);
    if (!foa) return;

    const firstAmount = foa.amounts?.[0];
    if (watchedItems?.length) {
      setValue('items.0.rateUSD', firstAmount?.amount ?? foa.amount1 ?? foa.amount ?? 0);
    }

    // Vessel/HQ from the FOA's branch relation
    const foaBranchId = foa.branch?.documentId
      || (typeof foa.branchId === 'string' ? foa.branchId : undefined);
    if (foaBranchId) setValue('vesselId', foaBranchId);

    // Project field carries the FOA number (persisted as text on the invoice)
    if (foa.foaNumber) setValue('projectId', foa.foaNumber);

    // Contract number/title from the FOA's contract relation (if any)
    const contractLabel = foa.contract?.title || foa.contract?.contractNumber;
    if (contractLabel) setValue('contractNumber', contractLabel);

    // Currency: prefer the first FOA amount's currency, else the FOA currency relation
    const foaCurrencyId = firstAmount?.currencyId
      || resolveCurrencyDocumentId(foa.currency, currencies as any[]);
    if (foaCurrencyId) setValue('currency', foaCurrencyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFoaId, foas, currencies]);

  useEffect(() => {
    // Only call setValue when the computed value actually changed, otherwise
    // this effect re-triggers itself endlessly and crashes the page
    let total = 0;
    if (isDebitOrCredit) {
      total = watchedItems?.reduce((s: number, i: any) => s + (Number(i.amountUSD) || 0), 0) || 0;
    } else {
      (watchedItems || []).forEach((item, idx) => {
        const days = item.fromDate && item.toDate
          ? Math.ceil((new Date(item.toDate).getTime() - new Date(item.fromDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
          : Number(item.quantity) || 0;
        const amt = days * (Number(item.rateUSD) || 0);
        if (Number(item.quantity) !== days) setValue(`items.${idx}.quantity`, days);
        if (Number(item.amountUSD) !== amt) setValue(`items.${idx}.amountUSD`, amt);
        total += amt;
      });
    }
    if (Number(watch('netTotal')) !== total) setValue('netTotal', total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedItems), setValue, isDebitOrCredit]);

  const addItem = () => {
    append({
      id: Date.now().toString(), itemNumber: fields.length + 1,
      description: getDefaultDescription(invoiceType),
      fromDate: new Date().toISOString().split('T')[0], toDate: new Date().toISOString().split('T')[0],
      unit: getDefaultUnit(invoiceType), quantity: 1, rateUSD: 0, amountUSD: 0
    } as InvoiceHireItem);
  };

  const removeItem = (index: number) => { if (fields.length > 1) remove(index); };

  const handleCodePrefixChange = (prefixId: string) => {
    setSelectedCodePrefix(prefixId);
    const p = findCodePrefix(codePrefixes as any[], prefixId);
    if (p) { const n = String(nextSequence(p)).padStart(3, '0'); setInvoiceSeqNumber(n); setValue('invoiceNumber', `${p.prefix}${n}`, { shouldValidate: true }); }
  };

  const onFormSubmit = (data: InvoiceHire) => {
    onSubmit({
      ...data, invoiceDate: formatDateForAPI(data.invoiceDate) || '',
      operationDate: formatMonthForAPI(data.operationDate) || '',
      items: data.items.map((item, i) => ({
        ...item, id: item.id || Date.now().toString() + i, itemNumber: i + 1,
        fromDate: formatDateForAPI(item.fromDate) || '', toDate: formatDateForAPI(item.toDate) || ''
      })),
      socialInsurance: !isDebitOrCredit && data.socialInsurance ? {
        ...data.socialInsurance,
        clientDeliveryDate: formatDateForAPI(data.socialInsurance.clientDeliveryDate) || '',
        insuranceDeliveryDate: formatDateForAPI(data.socialInsurance.insuranceDeliveryDate) || '',
        clientReceiptDate: formatDateForAPI(data.socialInsurance.clientReceiptDate) || ''
      } : undefined
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {invoiceHire ? 'Edit Invoice Hire/Acm' : 'Create Invoice Hire/Acm'}
          </h1>
          <p className="text-gray-600">
            {invoiceHire ? 'Update invoice details' : 'Create a new hire or accommodation invoice'}
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <InvoiceHireHeaderSection register={register} errors={errors} invoiceHire={invoiceHire}
          invoiceTypeOptions={invoiceTypeOptions} modeOfPayOptions={modeOfPayOptions}
          statusOptions={statusOptions} clientOptions={clientOptions} vesselOptions={vesselOptions}
          currencyOptions={currencyOptions} foaOptions={foaOptions} codePrefixes={codePrefixes as any[]}
          selectedCodePrefix={selectedCodePrefix} invoiceSeqNumber={invoiceSeqNumber}
          onCodePrefixChange={handleCodePrefixChange} selectedFoaId={selectedFoaId as string | undefined} foas={foas} />
        {isDebitOrCredit && (
          <InvoiceHireDebitNoteContent register={register} watch={watch} isDebitNote={isDebitNote} clients={clients} />
        )}
        <InvoiceHireItemsTable register={register} watch={watch} fields={fields}
          isDebitOrCredit={isDebitOrCredit} invoiceType={invoiceType} currencyOptions={currencyOptions}
          onAddItem={addItem} onRemoveItem={removeItem}
          getDefaultDescription={getDefaultDescription} getDefaultUnit={getDefaultUnit} />
        {!isDebitOrCredit && <InvoiceHireSocialInsurance register={register} />}
        <InvoiceHireSignaturesSection register={register} errors={errors} isDebitOrCredit={isDebitOrCredit} />
        <div className="flex justify-end space-x-4">
          <Button type="button" onClick={onCancel} variant="secondary">Cancel</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : invoiceHire ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceHireForm;
